import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Package, Truck, Briefcase, MessageSquare, 
  LogOut, Search, Phone, MessageCircle, Clock,
  CheckCircle, XCircle, AlertCircle, ChevronRight, User,
  RefreshCw, Loader2, Menu, X, Gift, MapPin, Send,
  Settings, Megaphone, Users, BarChart3, Mail, FileText,
  TrendingUp, TrendingDown, Calendar, Tag, Bell, Play, Pause,
  Plus, Edit, Trash2, Eye, ChevronDown, Filter, Download,
  Workflow, DollarSign, Inbox, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import AdminWalkthrough, { WALKTHROUGH_KEY } from '@/components/AdminWalkthrough';

// Sub-components
import SettingsPanel from './SettingsPanel';
import CampaignsPanel from './CampaignsPanel';
import TemplatesPanel from './TemplatesPanel';
import CRMPanel from './CRMPanel';
import AnalyticsPanel from './AnalyticsPanel';
import WorkflowsPanel from './WorkflowsPanel';
import MessageQueuePanel from './MessageQueuePanel';
import RevenuePanel from './RevenuePanel';
import ReviewsPanel from './ReviewsPanel';
import UsersPanel from './UsersPanel';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [isAuthenticated, setIsAuthenticated] = useState(!!location.state?.user);
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  
  // Tracking update state
  const [trackingMessage, setTrackingMessage] = useState('');
  const [trackingLocation, setTrackingLocation] = useState('');
  const [isAddingUpdate, setIsAddingUpdate] = useState(false);
  
  // Active view state
  const [activeView, setActiveView] = useState('dashboard');
  
  const [filters, setFilters] = useState({
    search: '',
    service: 'all',
    status: 'all'
  });
  
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await api.get('/api/auth/me');
        if (response.data.success) {
          setUser(response.data.user);
          setIsAuthenticated(true);
        } else {
          throw new Error('Not authenticated');
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/admin/login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  // Fetch leads and stats when authenticated
  const fetchLeads = useCallback(async () => {
    setIsLoadingLeads(true);
    try {
      const response = await api.get('/api/admin/leads');
      if (response.data.success) {
        setLeads(response.data.leads);
      }
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setIsLoadingLeads(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      // Stats fetch failed silently
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
      fetchStats();
    }
  }, [isAuthenticated, fetchLeads, fetchStats]);

  const handleAddTrackingUpdate = async (lead) => {
    if (!trackingMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsAddingUpdate(true);
    try {
      const bookingRef = lead.quote_id || lead.application_id || lead.submission_id;
      await api.post(`/api/admin/bookings/${bookingRef}/update`, {
        message: trackingMessage,
        location: trackingLocation || null
      });
      
      toast.success('Tracking update added');
      setTrackingMessage('');
      setTrackingLocation('');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to add tracking update');
    } finally {
      setIsAddingUpdate(false);
    }
  };

  const handleStatusUpdate = async (lead, newStatus) => {
    try {
      const type = lead.quote_id?.startsWith('DLV') ? 'delivery' 
        : lead.quote_id?.startsWith('MMV') ? 'minimove'
        : lead.application_id ? 'business'
        : 'contact';
      const id = lead.quote_id || lead.application_id || lead.submission_id;
      
      await api.put(`/api/admin/leads/${type}/${id}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Logout failed silently
    }
    navigate('/admin/login');
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !filters.search || 
      lead.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      lead.mobile?.includes(filters.search) ||
      lead.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      lead.quote_id?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesService = filters.service === 'all' ||
      (filters.service === 'delivery' && lead.quote_id?.startsWith('DLV')) ||
      (filters.service === 'mini-move' && lead.quote_id?.startsWith('MMV')) ||
      (filters.service === 'business' && lead.application_id) ||
      (filters.service === 'contact' && lead.submission_id);
    
    const matchesStatus = filters.status === 'all' || lead.status === filters.status;
    
    return matchesSearch && matchesService && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'contacted': 'bg-purple-100 text-purple-800',
      'quoted': 'bg-orange-100 text-orange-800',
      'confirmed': 'bg-green-100 text-green-800',
      'in_transit': 'bg-cyan-100 text-cyan-800',
      'completed': 'bg-green-100 text-green-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getServiceIcon = (lead) => {
    if (lead.quote_id?.startsWith('DLV')) return <Package className="h-4 w-4" />;
    if (lead.quote_id?.startsWith('MMV')) return <Truck className="h-4 w-4" />;
    if (lead.application_id) return <Briefcase className="h-4 w-4" />;
    return <MessageSquare className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, walkthrough: 'nav-dashboard' },
    { id: 'leads', label: 'Leads', icon: Package, walkthrough: 'nav-leads' },
    { id: 'crm', label: 'CRM', icon: Users, walkthrough: 'nav-crm' },
    { id: 'workflows', label: 'Workflows', icon: Workflow, walkthrough: 'nav-workflows' },
    { id: 'message-queue', label: 'Message Queue', icon: Inbox, walkthrough: 'nav-message-queue' },
    { id: 'campaigns', label: 'Campaigns', icon: Megaphone, walkthrough: 'nav-campaigns' },
    { id: 'templates', label: 'Templates', icon: FileText, walkthrough: 'nav-templates' },
    { id: 'reviews', label: 'Reviews', icon: Star, walkthrough: 'nav-reviews' },
    { id: 'revenue', label: 'Revenue & Cohorts', icon: DollarSign, walkthrough: 'nav-revenue' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, walkthrough: 'nav-analytics' },
    { id: 'users', label: 'Users', icon: Users, walkthrough: 'nav-users' },
    { id: 'settings', label: 'Settings', icon: Settings, walkthrough: 'nav-settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#001A47] transform transition-transform duration-200 ease-in-out
        lg:translate-x-0 lg:static lg:inset-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="block">
              <h1 className="text-2xl font-bold text-white">Rocket Van Express</h1>
              <p className="text-[#fb6402] text-sm font-semibold">Admin Console</p>
            </Link>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Button 
                key={item.id}
                variant="ghost" 
                className={`w-full justify-start text-white hover:bg-white/10 ${
                  activeView === item.id ? 'bg-white/10 text-[#fb6402]' : 'text-white/70'
                }`}
                onClick={() => { setActiveView(item.id); setSidebarOpen(false); }}
                data-walkthrough={item.walkthrough}
              >
                <item.icon className="mr-3 h-4 w-4" />
                {item.label}
              </Button>
            ))}
            
            <div className="pt-4 mt-4 border-t border-white/10">
              <p className="px-3 mb-2 text-xs font-semibold text-white/40 uppercase">Quick Filters</p>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => { setActiveView('leads'); setFilters(f => ({ ...f, service: 'delivery' })); }}
              >
                <Package className="mr-3 h-4 w-4" />
                Deliveries
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => { setActiveView('leads'); setFilters(f => ({ ...f, service: 'mini-move' })); }}
              >
                <Truck className="mr-3 h-4 w-4" />
                Mini-Moves
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => { setActiveView('leads'); setFilters(f => ({ ...f, service: 'business' })); }}
              >
                <Briefcase className="mr-3 h-4 w-4" />
                Business Apps
              </Button>
            </div>
          </nav>

          {/* User */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#fb6402] flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-white/50 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b px-4 lg:px-8 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h2 className="text-xl font-semibold text-[#001A47] capitalize">
                {activeView === 'crm' ? 'Customer Relationship Management' : activeView.replace('-', ' ')}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs text-gray-500"
                onClick={() => { localStorage.removeItem(WALKTHROUGH_KEY); window.location.reload(); }}
                data-testid="restart-tour-btn"
              >
                <AlertCircle className="h-3 w-3 mr-1" />
                Tour
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { fetchLeads(); fetchStats(); }}
                disabled={isLoadingLeads}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingLeads ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {/* Dashboard View */}
          {activeView === 'dashboard' && (
            <DashboardView 
              stats={stats} 
              leads={leads}
              onViewChange={setActiveView}
            />
          )}

          {/* Leads View */}
          {activeView === 'leads' && (
            <LeadsView 
              leads={filteredLeads}
              filters={filters}
              setFilters={setFilters}
              isLoading={isLoadingLeads}
              onSelectLead={(lead) => { setSelectedLead(lead); setShowLeadModal(true); }}
              getStatusColor={getStatusColor}
              getServiceIcon={getServiceIcon}
            />
          )}

          {/* CRM View */}
          {activeView === 'crm' && <CRMPanel />}

          {/* Campaigns View */}
          {activeView === 'campaigns' && <CampaignsPanel />}

          {/* Templates View */}
          {activeView === 'templates' && <TemplatesPanel />}

          {/* Reviews View */}
          {activeView === 'reviews' && <ReviewsPanel />}

          {/* Workflows View */}
          {activeView === 'workflows' && <WorkflowsPanel />}

          {/* Message Queue View */}
          {activeView === 'message-queue' && <MessageQueuePanel />}

          {/* Revenue & Cohorts View */}
          {activeView === 'revenue' && <RevenuePanel />}

          {/* Analytics View */}
          {activeView === 'analytics' && <AnalyticsPanel />}
          {activeView === 'users' && <UsersPanel currentUser={user} />}

          {/* Settings View */}
          {activeView === 'settings' && <SettingsPanel />}
        </div>
      </main>

      {/* Lead Detail Modal */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>

      {/* Admin Walkthrough */}
      <AdminWalkthrough activeView={activeView} onNavigate={setActiveView} />
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLead && getServiceIcon(selectedLead)}
              Lead Details
            </DialogTitle>
            <DialogDescription>
              {selectedLead?.quote_id || selectedLead?.application_id || selectedLead?.submission_id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{selectedLead.name || selectedLead.contact_person}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Mobile</p>
                  <p className="font-medium">{selectedLead.mobile}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedLead.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getStatusColor(selectedLead.status)}>
                    {selectedLead.status}
                  </Badge>
                </div>
              </div>

              {/* Service Details */}
              {(selectedLead.pickup || selectedLead.dropoff) && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Service Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Pickup</p>
                      <p>{selectedLead.pickup}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Drop-off</p>
                      <p>{selectedLead.dropoff}</p>
                    </div>
                    {selectedLead.items && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Items</p>
                        <p>{selectedLead.items || selectedLead.item}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Update Status */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Update Status</h4>
                <Select 
                  value={selectedLead.status}
                  onValueChange={(value) => handleStatusUpdate(selectedLead, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="quoted">Quoted</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Add Tracking Update */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Add Tracking Update</h4>
                <div className="space-y-3">
                  <Input
                    placeholder="Location (optional)"
                    value={trackingLocation}
                    onChange={(e) => setTrackingLocation(e.target.value)}
                  />
                  <Textarea
                    placeholder="Status message..."
                    value={trackingMessage}
                    onChange={(e) => setTrackingMessage(e.target.value)}
                  />
                  <Button 
                    onClick={() => handleAddTrackingUpdate(selectedLead)}
                    disabled={isAddingUpdate}
                    className="w-full"
                  >
                    {isAddingUpdate ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Add Update
                  </Button>
                </div>
              </div>

              {/* Tracking History */}
              {selectedLead.updates && selectedLead.updates.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Tracking History</h4>
                  <div className="space-y-2">
                    {selectedLead.updates.map((update, idx) => (
                      <div key={`${update.timestamp}-${idx}`} className="text-sm bg-gray-50 p-3 rounded">
                        <p className="text-gray-500 text-xs">
                          {new Date(update.timestamp).toLocaleString()}
                          {update.location && ` • ${update.location}`}
                        </p>
                        <p>{update.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="border-t pt-4 flex gap-2">
                <Button variant="outline" className="flex-1" asChild>
                  <a href={`tel:${selectedLead.mobile}`}>
                    <Phone className="h-4 w-4 mr-2" />
                    Call
                  </a>
                </Button>
                <Button variant="outline" className="flex-1 bg-green-50 text-green-700 hover:bg-green-100" asChild>
                  <a 
                    href={`https://wa.me/${selectedLead.mobile?.replace(/[^0-9]/g, '')}?text=Hi ${selectedLead.name}, regarding your Rocket Van Express booking...`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Dashboard View Component
const DashboardView = ({ stats, leads, onViewChange }) => {
  const todayLeads = leads.filter(l => {
    const created = new Date(l.created_at);
    const today = new Date();
    return created.toDateString() === today.toDateString();
  });

  const pendingLeads = leads.filter(l => l.status === 'new' || l.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onViewChange('leads')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Leads</p>
                <p className="text-3xl font-bold text-[#001A47]">{stats?.total || leads.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onViewChange('leads')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today</p>
                <p className="text-3xl font-bold text-[#fb6402]">{todayLeads.length}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onViewChange('leads')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{pendingLeads.length}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onViewChange('analytics')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-3xl font-bold text-green-600">
                  {leads.filter(l => l.status === 'completed' || l.status === 'delivered').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-[#fb6402]" onClick={() => onViewChange('campaigns')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Megaphone className="h-8 w-8 text-[#fb6402]" />
              <div>
                <p className="font-semibold">Campaigns</p>
                <p className="text-sm text-gray-500">Manage promotions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-purple-500" onClick={() => onViewChange('templates')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-purple-500" />
              <div>
                <p className="font-semibold">Templates</p>
                <p className="text-sm text-gray-500">Email & WhatsApp</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-cyan-500" onClick={() => onViewChange('crm')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-cyan-500" />
              <div>
                <p className="font-semibold">CRM</p>
                <p className="text-sm text-gray-500">Customer management</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500" onClick={() => onViewChange('settings')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-green-500" />
              <div>
                <p className="font-semibold">Settings</p>
                <p className="text-sm text-gray-500">System config</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Leads</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => onViewChange('leads')}>
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {leads.slice(0, 5).map((lead) => (
              <div key={lead.quote_id || lead.application_id || lead.submission_id} 
                   className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-full">
                    {lead.quote_id?.startsWith('DLV') ? <Package className="h-4 w-4 text-blue-600" /> :
                     lead.quote_id?.startsWith('MMV') ? <Truck className="h-4 w-4 text-purple-600" /> :
                     lead.application_id ? <Briefcase className="h-4 w-4 text-orange-600" /> :
                     <MessageSquare className="h-4 w-4 text-gray-600" />}
                  </div>
                  <div>
                    <p className="font-medium">{lead.name || lead.contact_person}</p>
                    <p className="text-sm text-gray-500">{lead.mobile}</p>
                  </div>
                </div>
                <Badge className={
                  lead.status === 'new' ? 'bg-blue-100 text-blue-800' :
                  lead.status === 'completed' || lead.status === 'delivered' ? 'bg-green-100 text-green-800' :
                  'bg-yellow-100 text-yellow-800'
                }>
                  {lead.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// Leads View Component
const LeadsView = ({ leads, filters, setFilters, isLoading, onSelectLead, getStatusColor, getServiceIcon }) => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, phone, email, or reference..."
                  className="pl-10"
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                />
              </div>
            </div>
            <Select value={filters.service} onValueChange={(v) => setFilters(f => ({ ...f, service: v }))}>
              <SelectTrigger className="w-full lg:w-40">
                <SelectValue placeholder="Service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
                <SelectItem value="mini-move">Mini-Move</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="contact">Contact</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger className="w-full lg:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <p className="text-sm text-gray-500">
        Showing {leads.length} lead{leads.length !== 1 ? 's' : ''}
      </p>

      {/* Leads Table */}
      <Card>
        <CardContent className="pt-6 overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No leads found matching your filters
            </div>
          ) : (
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Reference</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Customer</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Service</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.quote_id || lead.application_id || lead.submission_id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => onSelectLead(lead)}>
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm">
                        {lead.quote_id || lead.application_id || lead.submission_id}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{lead.name || lead.contact_person}</p>
                        <p className="text-sm text-gray-500">{lead.mobile}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {getServiceIcon(lead)}
                        <span className="text-sm">
                          {lead.quote_id?.startsWith('DLV') ? 'Delivery' :
                           lead.quote_id?.startsWith('MMV') ? 'Mini-Move' :
                           lead.application_id ? 'Business' : 'Contact'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" asChild>
                          <a href={`tel:${lead.mobile}`}>
                            <Phone className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button variant="ghost" size="icon" className="text-green-600" asChild>
                          <a 
                            href={`https://wa.me/${lead.mobile?.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
